// The admin console's Tutorials tab (ART-293): the list behind Settings › Tutorials. Each row is a title, an order
// number, a YouTube video and the audiences that see it; a seat sees the rows naming its own audience (a band seat
// by its role — Artist/AV, Band Member, Tour Manager, Merch Manager, Production or Content — an org seat by its org
// type), in order-number order.
import { useState, type FormEvent } from "react";
import { Pencil, Trash2 } from "lucide-react";
import {
  AcDialog, Button, Card, Checkbox, DataTable, DialogCancel, EmptyState, Field, FormDialog, IconButton, Tag,
  type DataTableColumn,
} from "@cntrl/ui";
import { errorMessage, formErrorMessage } from "../../app/hooks/apiError.js";
import {
  useAdminTutorials, useDeleteAdminTutorial, useSaveAdminTutorial, type AdminTutorialRow,
} from "../../app/hooks/useAdmin.js";
import { youTubeWatchUrl } from "../../app/vendorLinks.js";
import { ago } from "./adminFormat.js";
import styles from "./AdminDashboard.module.scss";

/** The audiences, in the server's canonical order (`TutorialAudiences.All`), with the word the console uses for each.
 *  The first six are the band seats, by role; the server files a Personal Assistant under Tour Manager and general
 *  crew under Production. */
const ARTIST_AUDIENCES = [
  { key: "artist_av", label: "Artist/AV" },
  { key: "band_member", label: "Band Member" },
  { key: "tour_manager", label: "Tour Manager" },
  { key: "merch_manager", label: "Merch Manager" },
  { key: "production", label: "Production" },
  { key: "content", label: "Content" },
] as const;

const TUTORIAL_AUDIENCES = [
  ...ARTIST_AUDIENCES,
  { key: "label", label: "Labels" },
  { key: "management", label: "Management" },
  { key: "booking", label: "Booking" },
] as const;

const audienceLabel = (key: string) => TUTORIAL_AUDIENCES.find((a) => a.key === key)?.label ?? key;

type Editing = { row?: AdminTutorialRow } | null;

/** Add or edit one tutorial. The server parses the YouTube link and refuses anything that is not a video. */
function TutorialDialog({ row, nextPosition, onClose }: { row?: AdminTutorialRow; nextPosition: number; onClose: () => void }) {
  const save = useSaveAdminTutorial();
  const [title, setTitle] = useState(row?.title ?? "");
  const [position, setPosition] = useState(String(row?.position ?? nextPosition));
  const [url, setUrl] = useState(row ? youTubeWatchUrl(row.youtubeVideoId) : "");
  const [audiences, setAudiences] = useState<readonly string[]>(
    row?.audiences ?? ARTIST_AUDIENCES.map((a) => a.key),
  );
  const [proOnly, setProOnly] = useState(row?.proOnly ?? false);
  const [formError, setFormError] = useState<string | null>(null);

  const order = Number.parseInt(position, 10);
  const valid = title.trim().length > 0 && url.trim().length > 0 && audiences.length > 0 && Number.isFinite(order);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setFormError(null);
    save.mutate(
      {
        tutorialId: row?.tutorialId,
        input: { title: title.trim(), position: order, youtubeUrl: url.trim(), audiences: [...audiences], proOnly },
      },
      { onSuccess: onClose, onError: (err) => setFormError(formErrorMessage(err, "Couldn't save the tutorial.")) },
    );
  }

  // Kept in the canonical order, so the request and the row's tags read the same way the server stores them.
  const toggle = (key: string, on: boolean) =>
    setAudiences((prev) =>
      TUTORIAL_AUDIENCES.map((a) => a.key).filter((k) => (k === key ? on : prev.includes(k))),
    );

  return (
    <FormDialog
      title={row ? "Edit tutorial" : "Add tutorial"}
      size="sm"
      onClose={onClose}
      bodyAs="form"
      onSubmit={onSubmit}
      footer={
        <>
          <DialogCancel />
          <Button type="submit" loading={save.isPending} disabled={!valid}>
            {row ? "Save" : "Add tutorial"}
          </Button>
        </>
      }
    >
      <div className={styles.stack}>
        <Field label="Title" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
        <Field
          label="Order"
          type="number"
          inputMode="numeric"
          value={position}
          hint="Lower numbers are listed first."
          onChange={(e) => setPosition(e.target.value)}
        />
        <Field
          label="YouTube link"
          // Text rather than `url`: the server takes a link with no scheme ("youtu.be/…") or a bare id, which a
          // url input would refuse before it is sent.
          inputMode="url"
          value={url}
          placeholder="youtube.com/watch?v=…"
          onChange={(e) => setUrl(e.target.value)}
        />
        <div className={styles.checks} role="group" aria-labelledby="tutorial-audiences">
          <span id="tutorial-audiences" className={styles.checksLabel}>Shown to</span>
          {TUTORIAL_AUDIENCES.map((a) => (
            <Checkbox key={a.key} label={a.label} checked={audiences.includes(a.key)} onChange={(on) => toggle(a.key, on)} />
          ))}
        </div>
        <div className={styles.checks} role="group" aria-labelledby="tutorial-plan">
          <span id="tutorial-plan" className={styles.checksLabel}>Plan</span>
          <Checkbox label="Pro only" checked={proOnly} onChange={setProOnly} />
          <span className={styles.checksHint}>
            Shown only to Pro artists and their teams. Labels, management and booking see it in their own Settings.
          </span>
        </div>
        {formError && <p className={styles.error} role="alert">{formError}</p>}
      </div>
    </FormDialog>
  );
}

export function TutorialsTab() {
  const tutorials = useAdminTutorials();
  const remove = useDeleteAdminTutorial();
  const [editing, setEditing] = useState<Editing>(null);
  const [deleting, setDeleting] = useState<AdminTutorialRow | null>(null);
  const rows = tutorials.data ?? [];
  const nextPosition = rows.reduce((max, r) => Math.max(max, (r.position ?? 0) + 1), 1);

  const columns: DataTableColumn<AdminTutorialRow>[] = [
    { key: "order", header: "Order", align: "end", cell: (r) => r.position },
    {
      key: "title",
      header: "Title",
      cell: (r) => (
        <span className={styles.inline}>
          {r.title}
          {r.proOnly && <Tag tone="accent" size="sm">Pro</Tag>}
        </span>
      ),
    },
    {
      key: "video",
      header: "Video",
      cell: (r) => (
        <a className={styles.code} href={youTubeWatchUrl(r.youtubeVideoId)} target="_blank" rel="noreferrer">
          {r.youtubeVideoId}
        </a>
      ),
    },
    {
      key: "audiences",
      header: "Shown to",
      cell: (r) => (
        <span className={styles.inline}>
          {(r.audiences ?? []).map((a) => <Tag key={a} tone="neutral" size="sm">{audienceLabel(a)}</Tag>)}
        </span>
      ),
    },
    { key: "updated", header: "Updated", cell: (r) => ago(r.updatedOn) },
    {
      key: "actions",
      header: "",
      align: "end",
      cell: (r) => (
        <span className={styles.inline}>
          <IconButton icon={Pencil} label={`Edit ${r.title}`} size="sm" onClick={() => setEditing({ row: r })} />
          <IconButton icon={Trash2} label={`Delete ${r.title}`} size="sm" onClick={() => setDeleting(r)} />
        </span>
      ),
    },
  ];

  return (
    <Card
      title="Tutorials"
      subtitle="The videos on Settings › Tutorials. Each seat sees the ones shown to its audience, lowest order number first."
      actions={<Button size="sm" variant="primary" onClick={() => setEditing({})}>Add tutorial</Button>}
    >
      {tutorials.isLoading ? (
        <p className={styles.zero}>Loading…</p>
      ) : tutorials.isError ? (
        <EmptyState title="Couldn't load the tutorials" description={errorMessage(tutorials.error, "Please try again.")} />
      ) : rows.length === 0 ? (
        <EmptyState title="No tutorials yet" description="Settings shows no Tutorials tab until one is added." />
      ) : (
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.tutorialId} label="Tutorials" />
      )}

      {editing && <TutorialDialog row={editing.row} nextPosition={nextPosition} onClose={() => setEditing(null)} />}
      {deleting && (
        <AcDialog
          title="Delete tutorial?"
          message={`"${deleting.title}" will no longer appear in anyone's Settings.`}
          confirmLabel="Delete"
          tone="danger"
          loading={remove.isPending}
          onConfirm={() => remove.mutate(deleting.tutorialId, { onSuccess: () => setDeleting(null) })}
          onCancel={() => setDeleting(null)}
        />
      )}
    </Card>
  );
}
